import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import './Legal.css';

const ChildSafety: React.FC = () => {
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (language === 'en') {
    return (
      <div className="legal-container">
        <div className="legal-content">
          <h1>Child Safety Policy</h1>
          <p className="last-updated">Last updated: February 11, 2026</p>

          <section>
            <h2>1. Our Commitment</h2>
            <p>
              <strong>Bob Map</strong> is committed to the safety and protection of all users,
              particularly children and minors. We maintain a <strong>zero tolerance policy</strong> toward
              any form of child sexual abuse and exploitation (CSAE) on our platform.
            </p>
            <p>
              This policy complies with the requirements of the{' '}
              <a href="https://support.google.com/googleplay/android-developer/answer/14747720" target="_blank" rel="noopener noreferrer">
                Google Play Child Safety Standards
              </a>, applicable child safety laws, and international regulations for the protection of minors.
            </p>
          </section>

          <section>
            <h2>2. Prohibited Content and Behavior</h2>
            <p>
              The following are <strong>strictly prohibited</strong> on Bob Map:
            </p>
            <ul>
              <li>Any content depicting, promoting, or facilitating child sexual abuse or exploitation (CSAE)</li>
              <li>Child sexual abuse material (CSAM), in any form whatsoever</li>
              <li>Grooming behavior or any attempt to establish inappropriate contact with minors</li>
              <li>Sharing personal information about minors without parental consent</li>
              <li>Any use of the platform to locate, track, or target minors</li>
              <li>Any content sexualizing minors, whether real or fictional</li>
            </ul>
          </section>

          <section>
            <h2>3. Age Requirement</h2>
            <p>
              Users must be <strong>at least 18 years old</strong> to create a Bob Map account.
              Minors must obtain parental or legal guardian consent before using the service,
              as stated in our <Link to="/terms">Terms of Service</Link>.
            </p>
          </section>

          <section>
            <h2>4. Reporting Mechanism</h2>
            <p>
              Bob Map provides several ways to report concerning behavior or content:
            </p>
            <ul>
              <li>
                <strong>In-app reporting:</strong> A "Report this user" button is available on every
                user profile. You can select a reason and provide details about the issue.
              </li>
              <li>
                <strong>By email:</strong> Send a report to{' '}
                <a href="mailto:bobmap.contact@gmail.com">bobmap.contact@gmail.com</a> with as much
                detail as possible.
              </li>
              <li>
                <strong>Bug report:</strong> The bug report button (accessible from any page) can also
                be used to report safety concerns.
              </li>
            </ul>
            <p>
              All reports are treated <strong>confidentially</strong> and reviewed promptly.
            </p>
          </section>

          <section>
            <h2>5. Enforcement Measures</h2>
            <p>
              Upon receiving a report or detecting a violation, Bob Map will take the following actions:
            </p>
            <ul>
              <li><strong>Immediate removal</strong> of any CSAM content identified</li>
              <li><strong>Immediate suspension</strong> of the reported user's account pending investigation</li>
              <li><strong>Permanent ban</strong> for confirmed violations</li>
              <li><strong>Reporting to authorities:</strong> Confirmed CSAM cases are reported to the{' '}
                <a href="https://www.missingkids.org/gethelpnow/cybertipline" target="_blank" rel="noopener noreferrer">
                  National Center for Missing &amp; Exploited Children (NCMEC)
                </a>{' '}
                and/or the relevant national authority (PHAROS in France)
              </li>
              <li><strong>Cooperation</strong> with law enforcement during investigations</li>
            </ul>
          </section>

          <section>
            <h2>6. Content Moderation</h2>
            <p>
              Bob Map is a location-sharing social network among friends. The application does not
              allow public content posting (no public posts, photos, or messages). Interactions are
              limited to accepted friends, which significantly reduces the risk of abuse.
            </p>
            <p>
              Nevertheless, our team actively monitors reports and takes swift action against
              any concerning behavior.
            </p>
          </section>

          <section>
            <h2>7. Child Safety Point of Contact</h2>
            <p>
              For any child safety-related concern, you can contact our designated safety officer:
            </p>
            <p>
              <strong>Hugo Bachellier</strong> — Child Safety Officer<br />
              Email: <a href="mailto:bobmap.contact@gmail.com">bobmap.contact@gmail.com</a><br />
              General contact: <a href="mailto:bobmap.contact@gmail.com">bobmap.contact@gmail.com</a><br />
              Address: 9 Allee de la Fauvette, 37100 Tours, France
            </p>
            <p>
              Our safety team is available to respond to inquiries from Google Play,
              law enforcement, and any person wishing to report a child safety concern.
            </p>
          </section>

          <section>
            <h2>8. Prevention and Education</h2>
            <p>
              Bob Map is committed to:
            </p>
            <ul>
              <li>Informing its users about online safety best practices</li>
              <li>Making in-app reporting tools easily accessible</li>
              <li>Regularly reviewing and updating this policy</li>
              <li>Training its team on child safety and CSAM detection</li>
            </ul>
          </section>

          <section>
            <h2>9. External Resources</h2>
            <p>
              If you or someone you know is a victim of exploitation, please contact:
            </p>
            <ul>
              <li>
                <strong>NCMEC CyberTipline:</strong>{' '}
                <a href="https://www.missingkids.org/gethelpnow/cybertipline" target="_blank" rel="noopener noreferrer">
                  www.missingkids.org
                </a>
              </li>
              <li>
                <strong>PHAROS (France):</strong>{' '}
                <a href="https://www.internet-signalement.gouv.fr" target="_blank" rel="noopener noreferrer">
                  www.internet-signalement.gouv.fr
                </a>
              </li>
              <li>
                <strong>119 Enfance en danger (France):</strong> Call 119 (free, 24/7)
              </li>
            </ul>
          </section>

          <div className="legal-footer">
            <Link to="/terms" className="legal-link">Terms of Service</Link>
            <Link to="/privacy-policy" className="legal-link">Privacy Policy</Link>
            <Link to="/legal" className="legal-link">Legal Notice</Link>
          </div>
        </div>
      </div>
    );
  }

  // French version (default)
  return (
    <div className="legal-container">
      <div className="legal-content">
        <h1>Politique de Protection des Mineurs</h1>
        <p className="last-updated">Derniere mise a jour : 11 fevrier 2026</p>

        <section>
          <h2>1. Notre Engagement</h2>
          <p>
            <strong>Bob Map</strong> s'engage pour la securite et la protection de tous ses utilisateurs,
            en particulier les enfants et les mineurs. Nous maintenons une politique de{' '}
            <strong>tolerance zero</strong> envers toute forme d'abus sexuel et d'exploitation
            des enfants (CSAE) sur notre plateforme.
          </p>
          <p>
            Cette politique est conforme aux exigences des{' '}
            <a href="https://support.google.com/googleplay/android-developer/answer/14747720" target="_blank" rel="noopener noreferrer">
              Normes de securite des enfants de Google Play
            </a>, aux lois applicables en matiere de protection de l'enfance et aux reglementations
            internationales pour la protection des mineurs.
          </p>
        </section>

        <section>
          <h2>2. Contenus et Comportements Interdits</h2>
          <p>
            Les comportements suivants sont <strong>strictement interdits</strong> sur Bob Map :
          </p>
          <ul>
            <li>Tout contenu representant, promouvant ou facilitant l'abus sexuel ou l'exploitation d'enfants (CSAE)</li>
            <li>Le materiel d'abus sexuel sur enfants (CSAM), sous quelque forme que ce soit</li>
            <li>Le grooming ou toute tentative d'etablir un contact inapproprie avec des mineurs</li>
            <li>Le partage d'informations personnelles de mineurs sans consentement parental</li>
            <li>Toute utilisation de la plateforme pour localiser, suivre ou cibler des mineurs</li>
            <li>Tout contenu sexualisant des mineurs, qu'il soit reel ou fictif</li>
          </ul>
        </section>

        <section>
          <h2>3. Condition d'Age</h2>
          <p>
            Les utilisateurs doivent avoir <strong>au moins 18 ans</strong> pour creer un compte Bob Map.
            Les mineurs doivent obtenir le consentement de leurs parents ou tuteurs legaux
            avant d'utiliser le service, comme indique dans nos{' '}
            <Link to="/terms">Conditions Generales d'Utilisation</Link>.
          </p>
        </section>

        <section>
          <h2>4. Mecanisme de Signalement</h2>
          <p>
            Bob Map met a disposition plusieurs moyens pour signaler un comportement ou contenu preoccupant :
          </p>
          <ul>
            <li>
              <strong>Signalement in-app :</strong> Un bouton "Signaler cet utilisateur" est disponible
              sur chaque profil utilisateur. Vous pouvez selectionner un motif et fournir des details.
            </li>
            <li>
              <strong>Par email :</strong> Envoyez un signalement a{' '}
              <a href="mailto:bobmap.contact@gmail.com">bobmap.contact@gmail.com</a> avec autant de
              details que possible.
            </li>
            <li>
              <strong>Rapport de bug :</strong> Le bouton de signalement de bug (accessible depuis
              n'importe quelle page) peut egalement etre utilise pour signaler des problemes de securite.
            </li>
          </ul>
          <p>
            Tous les signalements sont traites de maniere <strong>confidentielle</strong> et examines rapidement.
          </p>
        </section>

        <section>
          <h2>5. Mesures d'Application</h2>
          <p>
            A la reception d'un signalement ou lors de la detection d'une violation,
            Bob Map prendra les mesures suivantes :
          </p>
          <ul>
            <li><strong>Suppression immediate</strong> de tout contenu CSAM identifie</li>
            <li><strong>Suspension immediate</strong> du compte de l'utilisateur signale en attente d'enquete</li>
            <li><strong>Bannissement permanent</strong> pour les violations confirmees</li>
            <li><strong>Signalement aux autorites :</strong> Les cas de CSAM confirmes sont signales au{' '}
              <a href="https://www.missingkids.org/gethelpnow/cybertipline" target="_blank" rel="noopener noreferrer">
                National Center for Missing &amp; Exploited Children (NCMEC)
              </a>{' '}
              et/ou a l'autorite nationale competente (PHAROS en France)
            </li>
            <li><strong>Cooperation</strong> avec les forces de l'ordre lors des enquetes</li>
          </ul>
        </section>

        <section>
          <h2>6. Moderation du Contenu</h2>
          <p>
            Bob Map est un reseau social de partage de localisation entre amis. L'application ne
            permet pas la publication de contenu public (pas de posts, photos ou messages publics).
            Les interactions sont limitees aux amis acceptes, ce qui reduit considerablement
            les risques d'abus.
          </p>
          <p>
            Neanmoins, notre equipe surveille activement les signalements et agit rapidement
            contre tout comportement preoccupant.
          </p>
        </section>

        <section>
          <h2>7. Point de Contact Securite des Enfants</h2>
          <p>
            Pour toute preoccupation liee a la securite des enfants, vous pouvez contacter
            notre responsable securite designe :
          </p>
          <p>
            <strong>Hugo Bachellier</strong> — Responsable Securite des Enfants<br />
            Email : <a href="mailto:bobmap.contact@gmail.com">bobmap.contact@gmail.com</a><br />
            Contact general : <a href="mailto:bobmap.contact@gmail.com">bobmap.contact@gmail.com</a><br />
            Adresse : 9 Allee de la Fauvette, 37100 Tours, France
          </p>
          <p>
            Notre equipe securite est disponible pour repondre aux demandes de Google Play,
            des forces de l'ordre et de toute personne souhaitant signaler un probleme
            de securite des enfants.
          </p>
        </section>

        <section>
          <h2>8. Prevention et Education</h2>
          <p>
            Bob Map s'engage a :
          </p>
          <ul>
            <li>Informer ses utilisateurs sur les bonnes pratiques de securite en ligne</li>
            <li>Rendre les outils de signalement facilement accessibles dans l'application</li>
            <li>Reviser et mettre a jour regulierement cette politique</li>
            <li>Former son equipe sur la securite des enfants et la detection de CSAM</li>
          </ul>
        </section>

        <section>
          <h2>9. Ressources Externes</h2>
          <p>
            Si vous ou une personne que vous connaissez est victime d'exploitation, veuillez contacter :
          </p>
          <ul>
            <li>
              <strong>PHAROS (France) :</strong>{' '}
              <a href="https://www.internet-signalement.gouv.fr" target="_blank" rel="noopener noreferrer">
                www.internet-signalement.gouv.fr
              </a>
            </li>
            <li>
              <strong>119 Enfance en danger :</strong> Appelez le 119 (gratuit, 24h/24)
            </li>
            <li>
              <strong>NCMEC CyberTipline :</strong>{' '}
              <a href="https://www.missingkids.org/gethelpnow/cybertipline" target="_blank" rel="noopener noreferrer">
                www.missingkids.org
              </a>
            </li>
          </ul>
        </section>

        <div className="legal-footer">
          <Link to="/terms" className="legal-link">Conditions d'Utilisation</Link>
          <Link to="/privacy-policy" className="legal-link">Politique de Confidentialite</Link>
          <Link to="/legal" className="legal-link">Mentions Legales</Link>
        </div>
      </div>
    </div>
  );
};

export default ChildSafety;
